/**
 * Échéances, responsabilités et prochaine action — chunk 9.AF.1.
 *
 * ## Ce que ce contrat répond
 *
 * Les cinq questions du conseiller, mais cette fois à l'échelle du temps : qui
 * attend après qui, quelle action est requise, qui en est responsable, quelle
 * échéance approche ou est dépassée, et quelle est la prochaine action.
 *
 * Tout est calculé côté serveur. Le frontend ne recalcule ni la priorité, ni la
 * qualification temporelle — décision PO, et propriété qui compte : le jour où le
 * seuil change, il change à un seul endroit.
 */

import { z } from "zod";

import { UuidSchema } from "./common.js";

/**
 * Qualification temporelle d'une échéance.
 *
 * `unassigned` n'est **pas** un état temporel, et c'est délibéré : une action sans
 * responsable est un problème indépendant de sa date. La signaler comme telle évite
 * qu'elle se noie parmi les `upcoming` — c'est précisément le cas où personne
 * n'attend, parce que personne ne sait que c'est à lui.
 */
export const DEADLINE_STATES = [
  "overdue",
  "due_soon",
  "upcoming",
  "unassigned",
  "resolved",
] as const;
export type DeadlineState = (typeof DEADLINE_STATES)[number];

/** Ce qui produit une échéance. */
export const DEADLINE_KINDS = [
  /** Information attendue du client. */
  "client_information",
  /** Élément qui incombe au cabinet. */
  "broker_action",
  /** Réponse attendue d'un assureur. */
  "insurer_response",
] as const;
export type DeadlineKind = (typeof DEADLINE_KINDS)[number];

/**
 * Qui attend après qui.
 *
 * Distinct du responsable : `waiting_on` dit **d'où viendra le déblocage**, le
 * responsable dit **qui doit s'en occuper chez nous**. Un dossier peut attendre le
 * client tout en ayant un conseiller responsable de la relance.
 */
export const WAITING_PARTIES = ["client", "broker", "insurer", "nobody"] as const;
export type WaitingParty = (typeof WAITING_PARTIES)[number];

/**
 * Une échéance, telle que l'écran la lit.
 *
 * `responsible_user_id` est `null` quand personne n'est désigné — jamais un
 * utilisateur choisi arbitrairement. Décision PO explicite : « jamais attribution
 * silencieuse ».
 */
export const DeadlineItemSchema = z.object({
  id: UuidSchema,
  kind: z.enum(DEADLINE_KINDS),
  state: z.enum(DEADLINE_STATES),
  waiting_on: z.enum(WAITING_PARTIES),
  label: z.string(),
  project_id: UuidSchema,
  project_title: z.string(),
  /**
   * 9.BH.1 — le client, et qui l'on attend.
   *
   * Sans eux, une ligne d'alerte dit « une réponse est attendue » et il faut
   * ouvrir le dossier pour savoir chez qui et de qui : exactement l'aller-retour
   * que le tableau de bord existe pour éviter. `awaited_insurers` ne nomme que
   * les destinataires ENCORE en attente, et vaut `null` hors demande.
   */
  company_name: z.string().nullable().optional(),
  awaited_insurers: z.string().nullable().optional(),
  due_at: z.string().nullable(),
  /** Jours restants — négatif si dépassé. `null` sans échéance. */
  days_remaining: z.number().int().nullable(),
  responsible_user_id: UuidSchema.nullable(),
  responsible_name: z.string().nullable(),
  /**
   * Le responsable désigné est-il désactivé ?
   *
   * L'alerte ne disparaît pas pour autant — décision PO : elle devient visiblement
   * à réassigner. Perdre une échéance parce que quelqu'un a quitté le cabinet est
   * exactement ce que ce chunk existe pour empêcher.
   */
  responsible_inactive: z.boolean(),
  /** Dernière relance envoyée pour cet objet, si elle existe. */
  last_reminder_at: z.string().nullable(),
  subject_kind: z.enum(["missing_information", "quote_request"]),
  subject_id: UuidSchema,
});
export type DeadlineItem = z.infer<typeof DeadlineItemSchema>;

/**
 * La prochaine action, décidée côté serveur.
 *
 * Hiérarchie fixée par la décision PO, du plus bloquant au moins urgent. Elle
 * répond à « par quoi je commence » — pas « qu'est-ce qui existe ».
 */
export const DEADLINE_NEXT_ACTIONS = [
  /** Quelque chose est dépassé et nous incombe. */
  "resolve_overdue_broker",
  /** Quelque chose est dépassé, chez un tiers. */
  "follow_up_overdue",
  /** Une action nous incombe, pas encore dépassée. */
  "complete_broker_items",
  /** On attend le client. */
  "follow_up_with_client",
  /** On attend un assureur. */
  "await_insurer",
  /** Des actions n'ont personne pour les porter. */
  "assign_owner",
  /** Rien à faire ici. */
  "nothing_pending",
] as const;
export type DeadlineNextAction = (typeof DEADLINE_NEXT_ACTIONS)[number];

/** Le résumé d'un dossier, ou du tableau de bord. */
export const DeadlineSummarySchema = z.object({
  overdue_count: z.number().int().nonnegative(),
  due_soon_count: z.number().int().nonnegative(),
  upcoming_count: z.number().int().nonnegative(),
  unassigned_count: z.number().int().nonnegative(),
  waiting_on: z.array(z.enum(WAITING_PARTIES)),
  next_action: z.enum(DEADLINE_NEXT_ACTIONS),
  items: z.array(DeadlineItemSchema),
});
export type DeadlineSummary = z.infer<typeof DeadlineSummarySchema>;

/** Une notification interne, telle que la cloche la lit. */
export const NotificationSchema = z.object({
  id: UuidSchema,
  type: z.string(),
  title: z.string(),
  body: z.string().nullable(),
  link_url: z.string().nullable(),
  project_id: UuidSchema.nullable(),
  subject_kind: z.string().nullable(),
  subject_id: UuidSchema.nullable(),
  read_at: z.string().nullable(),
  created_at: z.string(),
});
export type AppNotification = z.infer<typeof NotificationSchema>;

/**
 * Fenêtre « échéance proche », en jours.
 *
 * Sept jours, **repris de `DUE_SOON_WINDOW_DAYS`** posé au chunk 9.AC.1 plutôt que
 * choisi à nouveau. Deux seuils différents pour la même notion produiraient un
 * dossier « à échéance proche » sur un écran et « à venir » sur l'autre — la
 * divergence la plus facile à créer et la plus difficile à diagnostiquer.
 */
export { DUE_SOON_WINDOW_DAYS } from "./project-requirements.js";
